<template>
  <div class="main-content">
    <!-- Title -->
    <div>
      <b-row class="mb-16">
        <b-col md="8">
          <h2 class="page-title">
            User Time Logs
          </h2>
        </b-col>
      </b-row>
    </div>
    <!-- Filters -->
    <template>
      <b-row>
        <b-col md="6">
          <b-form-group label="Client">
            <b-form-select v-model="client" :options="clientOptions">
              <template v-slot:first>
                <option :value="null">-- Please select a Client --</option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Project">
            <b-form-select v-model="project" :options="projectOptions">
              <template v-slot:first>
                <option :value="null">-- Please select a Project --</option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group label="User">
            <b-form-select v-model="user" :options="userOptions">
              <template v-slot:first>
                <option :value="null">-- Please select a User --</option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Date Range" class="date">
            <v-date-picker
              v-model="dateRange"
              :first-day-of-week="1"
              mode="range"
              :input-props="{
                placeholder: 'Please select date range'
              }"
              :masks="{ input: 'YYYY/MM/DD' }"
            ></v-date-picker>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="10">
          <b-button
            pill
            variant="outline-primary ripple"
            class="clear-btn"
            size="sm"
            @click="clear"
            >Clear</b-button
          >
          <b-button
            pill
            :variant="getButtonVariant()"
            class="clear-btn m-lg-2"
            size="sm"
            @click="toggleShowCurrentUserlogs"
            >{{ getShowButtonText() }}</b-button
          >
          <b-button
            pill
            class="clear-btn m-lg-2"
            size="sm"
            @click="getExcelreport"
            >Export Excel</b-button
          >
        </b-col>
      </b-row>
    </template>
    <!-- Time Log Table -->
    <b-row class="table-row">
      <overflowMessage ref="overflowMessage" ref-name="userTimeLogs" />
      <b-table
        ref="userTimeLogs"
        :busy="timeLogsLoading"
        show-empty
        empty-text="No timeLogs found"
        hover
        bordered
        striped
        thead-class="cursor-pointer"
        sticky-header
        responsive
        :fields="fields"
        :items="items"
        :current-page="currentPage"
        :per-page="perPage"
      >
        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            variant="success"
            @click="showTimeLogModal(row.item)"
          >
            Edit
          </b-button>
        </template>
        <template v-slot:table-busy>
          <TableLoadingSkeleton />
        </template>
      </b-table>
      <Pagination
        :total-rows="totalRows"
        :page-options="pageOptions"
        :per-page="perPage"
        :current-page="currentPage"
      />
    </b-row>

    <!--    Time Log Modal-->
    <TimeLogModal
      ref="timeLogModal"
      :is-ticket="false"
      :edit-time-log="editTimeLog"
      :parent-client-options="clientOptions"
      @clear-edit-timeLog="clearEditTimeLog"
      @timeLog-updated="timeLogUpdated"
    />
  </div>
</template>

<script>
import momentBusiness from "moment-business-days"
import gql from "graphql-tag"
import TimeLogModal from "@/components/timeLog/timeLogModal"
import overflowMessage from "@/components/misc/overflowMessage"
import { mapGetters } from "vuex"
import TableLoadingSkeleton from "@/components/misc/tableLoadingSkeleton"
import Pagination from "@/components/misc/customPagination"

export default {
  name: "UserTimeLogs",
  components: {
    TimeLogModal,
    overflowMessage,
    TableLoadingSkeleton,
    Pagination
  },
  apollo: {
    clients: {
      query: gql`
        query {
          clients {
            id
            name
            projects {
              project
              status
              id
            }
          }
        }
      `,
      update(data) {
        return data?.clients.sortBy(x => x.name)
      }
    },
    users: {
      query: require("@/graphql/Account/GetUsers.gql"),
      update(data) {
        return data.users.sortBy(x => x.firstName)
      }
    },
    timeLogs: {
      query: require("@/graphql/TimeLog/GetTimeLogs.gql"),
      variables() {
        //This is put here because this is hit right when the query is called
        this.timeLogsLoading = true
        return this.filterModel
      },
      skip() {
        //Only fetch once initialized
        return (
          !this.filterModel?.filterModel?.to ||
          !this.filterModel?.filterModel?.from ||
          //Stops the initial fetch if user got here from showTimeButton
          this.$route.params.clientId
        )
      },
      update(data) {
        //This is put here because this is hit right when the query result is returned
        this.timeLogsLoading = false

        return data.getTimeLogs
      }
    }
  },
  data() {
    return {
      loading: true,
      isBusy: false,
      timeLogsLoading: true,
      client: null,
      project: null,
      user: null,
      dateRange: null,
      fields: null,
      editTimeLog: null,
      showCurrentUserlogs: false,
      currentPage: 1,
      perPage: 50,
      pageOptions: [15, 30, 50]
    }
  },
  computed: {
    ...mapGetters({
      getCurrentUser: "getCurrentUser"
    }),
    items() {
      return this.timeLogs
        ?.filter(
          x =>
            x?.user &&
            (!this.showCurrentUserlogs || x?.user?.id == this.getCurrentUser.id)
        )
        ?.map(x => {
          return {
            Id: x?.id,
            Date: this.$moment(x?.date).format("DD MMM"),
            Client: x?.client?.name ?? "",
            User: this.$utils.getUserName(x?.user),
            Project: x?.project?.project ?? "Support",
            Description: x?.description,
            Task: x?.task,
            TicketNumber: x?.ticketNumber ?? "",
            Billable: x?.type,
            Time: x?.time
          }
        })
    },
    clientOptions() {
      return this.clients?.map(client => {
        return { value: client, text: client.name }
      })
    },
    projectOptions() {
      return this.client?.projects?.map(project => {
        return {
          value: project,
          text: `${project.project} ${
            project.status != "Accepted" ? "- Closed" : ""
          }`
        }
      })
    },
    userOptions() {
      return this.users?.map(x => {
        return { value: x, text: this.$utils.getUserName(x) }
      })
    },
    filterModel() {
      return {
        filterModel: {
          from: this.dateRange?.start.asGMT(),
          to: this.dateRange?.end.asGMT(),
          userId: this.user?.id,
          projectId: this.project?.id,
          clientId: this.client?.id
        }
      }
    },
    timeLogReportApiLink() {
      return process.env.VUE_APP_FILE_GET_TIMELOG_REPORT_HTTPS
    },
    totalRows() {
      return this.items?.length ?? 0
    }
  },
  watch: {
    client() {
      this.project = null
    },
    items(val) {
      //When the table's items change update the overflowMessage component state
      setTimeout(() => {
        //Only update if there are items - prevents jumping between states when loading data
        if (val) {
          //this.$refs.overflowMessage.checkOverflowing()
          //this.$refs.overflowMessage.$forceUpdate()
        }
      }, 500)
    },
    clientOptions() {
      //ClientId from view time button
      const clientId = this.$route.params.clientId
      if (clientId) {
        this.client = this.clientOptions.find(x => x.value.id == clientId).value
        this.dateRange = {
          start: this.$moment(new Date())
            .subtract(1, "months")
            .endOf("month")
            .toDate(),
          end: momentBusiness(new Date(), "YYYY-DD-MM")
            .prevBusinessDay()
            .toDate()
        }
        //Prevent this from being done again
        this.$route.params.clientId = null
      }
    }
  },
  created() {
    this.setDateRange()
    //Set table fields
    this.fields = [
      { key: "Date", sortable: true },
      { key: "Client", sortable: true },
      { key: "User", sortable: true },
      { key: "Project", sortable: true },
      { key: "Description", sortable: true },
      { key: "Task", sortable: true },
      { key: "TicketNumber", sortable: true },
      { key: "Billable", sortable: true },
      { key: "Time", sortable: true },
      { key: "Actions" }
    ]
  },
  methods: {
    clear() {
      this.client = null
      this.project = null
      this.user = null
      this.showCurrentUserlogs = false
      this.setDateRange()
    },
    clearEditTimeLog() {
      this.editTimeLog = null
    },
    getShowButtonText() {
      return `Show ${this.showCurrentUserlogs ? "all" : "just mine"}`
    },
    getButtonVariant() {
      return `${this.showCurrentUserlogs ? "" : "outline-"}primary ripple `
    },
    toggleShowCurrentUserlogs() {
      this.showCurrentUserlogs = !this.showCurrentUserlogs
    },
    getExcelreport() {
      const projectId =
        this.project?.id == null ? "" : `&projectId=${this.project.id}`
      const clientId =
        this.client?.id == null ? "" : `&clientId=${this.client.id}`
      const userId = this.user?.id == null ? "" : `&userId=${this.user.id}&`

      window.location.href = `${
        this.timeLogReportApiLink
      }?from=${this.dateRange.start.toISOString()}&to=${this.dateRange.end.toISOString()}${userId +
        clientId +
        projectId}`
    },
    timeLogUpdated() {
      //Refresh timeLog table query
      this.$apollo.queries.timeLogs.refetch()
    },
    async showTimeLogModal({ Id }) {
      const { data } = await this.$apollo.query({
        query: require("@/graphql/TimeLog/GetTimeLog.gql"),
        variables: {
          id: Id
        },
        fetchPolicy: "no-cache"
      })
      this.editTimeLog = data.getTimeLog
      this.$refs.timeLogModal.show()
    },
    setDateRange() {
      //Set default date range to last 2 business days
      this.dateRange = {
        start: momentBusiness(new Date(), "YYYY-DD-MM")
          .prevBusinessDay()
          .prevBusinessDay()
          .toDate(),
        end: momentBusiness(new Date(), "YYYY-DD-MM")
          .prevBusinessDay()
          .toDate()
      }
    }
  }
}
</script>

<style scoped>
.table-row {
  margin-top: 2rem;
}
</style>
